import * as React from "react";
import {
    Box,
    Stack,
    Typography,
    Chip,
    IconButton,
    Divider,
    Button,
    Collapse,
    Tooltip,
} from "@mui/material";
import {
    KeyboardArrowDown as ChevronDown,
    Delete as DeleteIcon,
    Edit as EditIcon,
} from "@mui/icons-material";
import { colors, typography } from "../../styles/globals";
import { formatBusinessName } from "../../utils/nameFormatters";
import { isValidCompanyStatus } from "../../constants/entityStatusConstants";

const STATUS = {
    active: { bg: "#E8FAF0", fg: "#10A37F", br: "#B7E4CC" },
    inactive: { bg: "#FFE9E8", fg: "#B42318", br: "#F3B1AD" },
    warning: { bg: "#FFF4E5", fg: "#B54708", br: "#FAD2A8" },
    compliant: { bg: "#E8FAF0", fg: "#10A37F", br: "#B7E4CC" },
};

// Cache status colors to avoid repeated calculations
const statusColorCache = new Map();

const getStatusColor = (status) => {
    if (statusColorCache.has(status)) {
        return statusColorCache.get(status);
    }

    let result;
    if (!status || typeof status !== "string") {
        result = STATUS.inactive;
    } else {
        const isValid = isValidCompanyStatus(status);
        result = isValid ? STATUS.active : STATUS.inactive;
    }

    statusColorCache.set(status, result);
    return result;
};

// Cache formatted dates
const dateCache = new Map();

const formatDate = (iso) => {
    if (!iso) return "N/A";

    if (dateCache.has(iso)) {
        return dateCache.get(iso);
    }

    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
        dateCache.set(iso, "N/A");
        return "N/A";
    }

    const formatted = d.toLocaleDateString("en-US", { year: "numeric", month: "short" });
    dateCache.set(iso, formatted);
    return formatted;
};

// Pre-defined style objects (moved outside component to avoid recreation)
const chipBaseStyles = {
    height: 24,
    borderRadius: "999px",
    fontSize: 12,
    fontWeight: typography.fontWeights.medium,
};

const defaultChipStyles = {
    ...chipBaseStyles,
    backgroundColor: "#F3F4F6",
    color: colors.primary,
};

const statusChipBaseStyles = {
    ...chipBaseStyles,
    letterSpacing: 0.2,
    lineHeight: 1,
    px: 1.25,
    boxShadow: "inset 0 0 0 1px rgba(16,24,40,0.02)",
    maxWidth: "160px",
    "& .MuiChip-label": {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    }
};

const nameTypographyStyles = {
    fontSize: typography.fontSizes.small,
    fontWeight: typography.fontWeights.regular,
    color: colors.primary,
    mb: 1,
    lineHeight: 1.25,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
};

const dividerStyles = { borderColor: "#E0E2F1", mt: 1 };

// Memoized entity item with better prop comparison
const EntityItem = React.memo(({ entity, onRemove, onEdit }) => {
    const statusColors = getStatusColor(entity.status);
    const formattedName = formatBusinessName(entity.legal_name);
    const formattedDate = formatDate(entity.incorporation_date);

    const statusChipStyles = React.useMemo(() => ({
        ...statusChipBaseStyles,
        backgroundColor: statusColors.bg,
        color: statusColors.fg,
        border: `1px solid ${statusColors.br}`,
    }), [statusColors]);

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Typography
                    sx={{ ...nameTypographyStyles, flex: 1 }}
                    title={formattedName}
                >
                    {formattedName}
                </Typography>
                {(onEdit || onRemove) && (
                    <Box sx={{ display: 'flex', gap: 0.25, ml: 0.5, flexShrink: 0 }}>
                        {onEdit && (
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(entity); }} sx={{ p: 0.25, color: '#4566EB' }}>
                                <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        )}
                        {onRemove && (
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onRemove(entity); }} sx={{ p: 0.25, color: '#d32f2f' }}>
                                <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        )}
                    </Box>
                )}
            </Box>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip
                    label={entity.state_code || "—"}
                    size="small"
                    sx={defaultChipStyles}
                />
                <Chip
                    label={formattedDate}
                    size="small"
                    sx={defaultChipStyles}
                />
                <Tooltip
                    title={entity.status || "Unknown"}
                    arrow
                    enterDelay={0}
                    leaveDelay={0}
                >
                    <Chip
                        label={entity.status || "Unknown"}
                        size="small"
                        sx={statusChipStyles}
                    />
                </Tooltip>
            </Stack>
            <Divider sx={dividerStyles} />
        </Box>
    );
}, (prevProps, nextProps) => {
    const prev = prevProps.entity;
    const next = nextProps.entity;

    return (
        prev.business_entity_id === next.business_entity_id &&
        prev.legal_name === next.legal_name &&
        prev.state_code === next.state_code &&
        prev.incorporation_date === next.incorporation_date &&
        prev.status === next.status &&
        !!prevProps.onRemove === !!nextProps.onRemove &&
        !!prevProps.onEdit === !!nextProps.onEdit
    );
});

EntityItem.displayName = 'EntityItem';

// Pre-defined container styles
const containerStyles = {
    borderRadius: "16px",
    border: "1px solid #EAECF0",
    background: "linear-gradient(180deg, #FBFCFE 0%, #FFFFFF 60%)",
    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
    overflow: "hidden",
};

const headerStyles = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    px: 2.5,
    py: 1,
    cursor: "pointer",
    userSelect: "none",
    "&:hover": { backgroundColor: "#F6F8FB" },
};

const titleStyles = {
    fontSize: typography.fontSizes.base,
    fontWeight: typography.fontWeights.regular,
    color: colors.primary,
};

const iconButtonStyles = {
    transition: "transform 200ms ease",
};

const contentStyles = { px: 2.5, py: 2 };

const emptyStateStyles = { color: "#6B7280", fontSize: 14 };

const viewAllButtonStyles = {
    textTransform: "none",
    borderRadius: "999px",
    fontWeight: typography.fontWeights.regular,
    px: 1.75,
    height: 30,
    borderWidth: 1.5,
    borderColor: "#E0E2F1",
    color: "#101828",
    fontSize: 13,
    "&:hover": {
        borderColor: "#CBD5E1",
        backgroundColor: "#F8FAFC",
    },
};

const LinkedEntities = ({
    count,
    entities = [],
    onViewAll,
    maxVisible = 3,
    title = "Other linked entities",
    onRemoveEntity,
    onEditEntity,
}) => {
    const [open, setOpen] = React.useState(false);
    const [showAll, setShowAll] = React.useState(false);

    const total = count ?? entities.length;

    // Memoize sorted entities with deep comparison to prevent unnecessary re-sorts
    const sortedEntities = React.useMemo(() => {
        return [...entities].sort((a, b) => {
            const dateA = new Date(a.incorporation_date);
            const dateB = new Date(b.incorporation_date);
            return dateB - dateA;
        });
    }, [entities]);

    // Memoize visible entities
    const visible = React.useMemo(() => {
        return showAll ? sortedEntities : sortedEntities.slice(0, maxVisible);
    }, [sortedEntities, showAll, maxVisible]);

    const toggle = React.useCallback(() => setOpen(o => !o), []);

    const handleKeyDown = React.useCallback((e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
        }
    }, [toggle]);

    const handleViewAll = React.useCallback((e) => {
        e.stopPropagation();
        if (onViewAll) {
            onViewAll();
        } else {
            setShowAll(true);
        }
    }, [onViewAll]);

    // Memoize icon transform style
    const iconTransform = React.useMemo(() => ({
        ...iconButtonStyles,
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
    }), [open]);

    return (
        <Box sx={containerStyles}>
            {/* Header / trigger */}
            <Box
                role="button"
                aria-expanded={open}
                aria-controls="linked-entities-content"
                tabIndex={0}
                onClick={toggle}
                onKeyDown={handleKeyDown}
                sx={headerStyles}
            >
                <Typography sx={titleStyles}>
                    {title} ({total})
                </Typography>

                <IconButton
                    size="small"
                    disableRipple
                    aria-label={open ? "Collapse" : "Expand"}
                    sx={iconTransform}
                >
                    <ChevronDown />
                </IconButton>
            </Box>

            <Collapse in={open}>
                <Box id="linked-entities-content" sx={contentStyles}>
                    {visible.length === 0 ? (
                        <Typography sx={emptyStateStyles}>
                            No linked entities found.
                        </Typography>
                    ) : (
                        <Stack spacing={2}>
                            {visible.map((entity) => (
                                <EntityItem
                                    key={entity.business_entity_id}
                                    entity={entity}
                                    onRemove={onRemoveEntity}
                                    onEdit={onEditEntity}
                                />
                            ))}
                        </Stack>
                    )}

                    {/* View all button */}
                    {total > (showAll ? total : maxVisible) && (
                        <Box sx={{ mt: 2.5 }}>
                            <Button
                                type="button"
                                onClick={handleViewAll}
                                variant="outlined"
                                size="small"
                                sx={viewAllButtonStyles}
                            >
                                View all
                            </Button>
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
};

export default LinkedEntities;